import { useEffect, useRef, useState } from 'react';
import { createSilenceEnder, type SilenceEnder } from './silenceEnder.js';
import { holdOtaApply } from './native/ota.js';

/**
 * Captura de mic one-shot que produce un WAV PCM 16-bit mono 16kHz — el formato
 * que el endpoint de pronunciation assessment acepta directo, sin transcoding.
 *
 * Portado de `apps/client/.../PronunciationBlock.tsx` (`useMicCapture`) a un util
 * reusable. Usa Web Audio API (más portable que MediaRecorder en WKWebView/
 * Capacitor). Auto-stop configurable (~3.5s por defecto).
 *
 * Uso típico (drill): `const blob = await start();` — la promesa resuelve al
 * hacer `stop()` o al cumplirse el auto-stop. `null` si no se capturó nada.
 */

export type MicCaptureState = 'idle' | 'recording' | 'processing' | 'error';

export interface UseMicWavCaptureReturn {
  state: MicCaptureState;
  error: string | null;
  /**
   * Comienza a grabar y resuelve con el WAV al frenar (o `null` si vacío/error).
   * Con `rejectOnError`, un mic que no abre RECHAZA en vez de resolver `null`:
   * para quien tiene que distinguir «no se oyó nada» de «no hay mic».
   * `onAudio` recibe cada pedazo de la toma mientras graba (la escucha en vivo
   * del repaso), con la frecuencia a la que llegó.
   */
  start: (opts?: StartOptions) => Promise<Blob | null>;
  /** Frena la captura manualmente (antes del auto-stop). */
  stop: () => void;
  /**
   * Desde ahora, la toma se cierra sola cuando el alumno se calla `silenceMs`
   * después de hablar (`createSilenceEnder`; la voz desde el principio de la
   * toma cuenta). Sin toma en curso, no hace nada.
   */
  endOnSilence: (silenceMs: number) => void;
}

export interface StartOptions {
  rejectOnError?: boolean;
  onAudio?: (samples: Float32Array, sampleRate: number) => void;
}

const DEFAULT_MAX_MS = 3500;
const TARGET_SAMPLE_RATE = 16000;
const WAV_HEADER_BYTES = 44;

export function useMicWavCapture(maxMs: number = DEFAULT_MAX_MS): UseMicWavCaptureReturn {
  const [state, setState] = useState<MicCaptureState>('idle');
  const [error, setError] = useState<string | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const chunksRef = useRef<Float32Array[]>([]);
  const resolveRef = useRef<((blob: Blob | null) => void) | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Fuente de verdad SÍNCRONA (no la del render): un `stop()` en el mismo tick
  // que `start()` tiene que verla.
  const stateRef = useRef<MicCaptureState>(state);
  /** `stop()` llegó mientras el navegador todavía no entregaba el mic. */
  const stopRequestedRef = useRef(false);
  /** Sigue la energía de la toma para `endOnSilence`. */
  const silenceRef = useRef<SilenceEnder | null>(null);
  /** Native OTA hold of the take in flight (lib/native/ota.ts); released by `cleanup()`. */
  const otaReleaseRef = useRef<(() => void) | null>(null);
  const setPhase = (s: MicCaptureState): void => {
    stateRef.current = s;
    setState(s);
  };

  const cleanup = (): void => {
    otaReleaseRef.current?.();
    otaReleaseRef.current = null;
    silenceRef.current = null;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      void audioCtxRef.current.close();
    }
    audioCtxRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(
    () => () => {
      // Desmontado con el mic todavía en camino: `start()` lo cierra al llegar.
      stopRequestedRef.current = true;
      cleanup();
    },
    [],
  );

  const stop = (): void => {
    if (stateRef.current !== 'recording') return;
    if (!audioCtxRef.current) {
      // Soltó antes de que llegara el mic: `start()` lo cierra al llegar.
      stopRequestedRef.current = true;
      return;
    }
    setPhase('processing');
    const chunks = chunksRef.current;
    chunksRef.current = [];
    const inputRate = audioCtxRef.current?.sampleRate ?? TARGET_SAMPLE_RATE;
    cleanup();
    const merged = mergeFloat32(chunks);
    const resampled =
      inputRate === TARGET_SAMPLE_RATE ? merged : downsampleFloat32(merged, inputRate);
    const wav = floatToWav16(resampled);
    const blob = new Blob([wav], { type: 'audio/wav' });
    const cb = resolveRef.current;
    resolveRef.current = null;
    setPhase('idle');
    if (cb) cb(blob.size > WAV_HEADER_BYTES ? blob : null);
  };

  const start = async (opts?: StartOptions): Promise<Blob | null> => {
    if (stateRef.current === 'recording') return null;
    setError(null);
    stopRequestedRef.current = false;
    setPhase('recording');
    // No bundle update while the mic records: every exit (stop, auto-stop, error,
    // unmount) goes through `cleanup()`, which releases it.
    otaReleaseRef.current ??= holdOtaApply('mic-recording');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      if (stopRequestedRef.current) {
        stopRequestedRef.current = false;
        cleanup();
        setPhase('idle');
        return null;
      }
      const AudioCtxCls =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new AudioCtxCls({ sampleRate: TARGET_SAMPLE_RATE });
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      // ScriptProcessorNode está deprecado pero funciona en todos lados; AudioWorklet
      // requeriría un módulo aparte que complica el bundling de Speak.
      // 2048 (128 ms a 16 kHz): la escucha en vivo recibe el audio con poco atraso.
      const processor = ctx.createScriptProcessor(2048, 1, 1);
      processorRef.current = processor;
      chunksRef.current = [];
      silenceRef.current = createSilenceEnder();
      processor.onaudioprocess = (e: AudioProcessingEvent): void => {
        const samples = new Float32Array(e.inputBuffer.getChannelData(0));
        chunksRef.current.push(samples);
        opts?.onAudio?.(samples, ctx.sampleRate);
        if (silenceRef.current?.feed(samples, ctx.sampleRate)) {
          silenceRef.current = null;
          stop();
        }
      };
      source.connect(processor);
      processor.connect(ctx.destination);

      const done = new Promise<Blob | null>((resolve) => {
        resolveRef.current = resolve;
      });
      timeoutRef.current = setTimeout(() => stop(), maxMs);
      return await done;
    } catch (e) {
      setPhase('error');
      setError(e instanceof Error ? e.message : 'mic error');
      cleanup();
      if (opts?.rejectOnError) throw e;
      return null;
    }
  };

  const endOnSilence = (silenceMs: number): void => {
    silenceRef.current?.arm(silenceMs);
  };

  return { state, error, start, stop, endOnSilence };
}

/** Un pedazo de audio en PCM 16 kHz mono de 16 bits (little-endian), sin cabecera. */
export function toPcm16k(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const at16k =
    sampleRate === TARGET_SAMPLE_RATE ? samples : downsampleFloat32(samples, sampleRate);
  const out = new Int16Array(at16k.length);
  for (let i = 0; i < at16k.length; i++) {
    const s = Math.max(-1, Math.min(1, at16k[i]!));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out.buffer;
}

function mergeFloat32(chunks: Float32Array[]): Float32Array {
  let total = 0;
  for (const c of chunks) total += c.length;
  const out = new Float32Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out;
}

/** Downsample por interpolación lineal a 16kHz. Suficiente para PA scoring. */
function downsampleFloat32(input: Float32Array, fromRate: number): Float32Array {
  if (fromRate === TARGET_SAMPLE_RATE) return input;
  const ratio = fromRate / TARGET_SAMPLE_RATE;
  const outLength = Math.floor(input.length / ratio);
  const out = new Float32Array(outLength);
  for (let i = 0; i < outLength; i++) {
    const idx = i * ratio;
    const lo = Math.floor(idx);
    const hi = Math.min(input.length - 1, lo + 1);
    const frac = idx - lo;
    out[i] = input[lo]! * (1 - frac) + input[hi]! * frac;
  }
  return out;
}

/** WAV PCM 16-bit LE mono 16kHz: header (44b) + datos. */
function floatToWav16(samples: Float32Array): ArrayBuffer {
  const bytesPerSample = 2;
  const dataLength = samples.length * bytesPerSample;
  const buffer = new ArrayBuffer(WAV_HEADER_BYTES + dataLength);
  const view = new DataView(buffer);
  const writeStr = (offset: number, str: string): void => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true); // subchunk1size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, TARGET_SAMPLE_RATE, true);
  view.setUint32(28, TARGET_SAMPLE_RATE * bytesPerSample, true); // byte rate
  view.setUint16(32, bytesPerSample, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeStr(36, 'data');
  view.setUint32(40, dataLength, true);
  let offset = WAV_HEADER_BYTES;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]!));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }
  return buffer;
}
